import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import type { PersonaSourceStillProgress } from "../api/types.js";
import { formatElapsed, formatRelative } from "../lib/time-format.js";
import { ManualSourceFramePicker } from "./ManualSourceFramePicker.js";
import { SubjectSelector } from "./SubjectSelector.js";
import { MilestoneStrip } from "./MilestoneStrip.js";
import { clearFrameSelectionPending, markFrameSelectionPending, pendingFrameSelectionStartedAtMs } from "../lib/gate1-select-pending.js";

/**
 * P0 UX fix (2026-09): renders `PersonaSourceStillProgress` — every field
 * on it is derived from durable Postgres state (see
 * `derivePersonaSourceStillProgress` on the backend), so a page refresh
 * always reconstructs the exact same view from one GET call. The ONLY
 * thing computed client-side is the ELAPSED-TIME label, which is
 * explicitly allowed to use wall-clock time because it is labeled
 * "Elapsed", never presented as a completion percentage.
 *
 * Truthful freshness correction (2026-09-07, finding #2): `progress.updatedAtIso`
 * is a DURABLE STATE timestamp — when the backend row last actually
 * changed — never "when we last successfully checked." The Gate 1
 * reconciliation loop (`AdvancePersonaSourceStillVisualModification`)
 * genuinely polls WaveSpeed every 15s but returns the artifact UNCHANGED
 * (no save at all) while a prediction is still `"created"`/`"processing"`
 * — so a perfectly healthy, actively-polled artifact can show a
 * stale-looking "no update in 4 minutes" purely because nothing has
 * changed yet, falsely implying monitoring stopped. `lastCheckedAtMs` is
 * a NEW, separate, purely client-tracked timestamp — the caller's own
 * `Date.now()` at its last SUCCESSFUL poll, set only in a fetch's success
 * branch, never in its catch — deliberately never persisted (no new DB
 * column/table needed; the existing 15s polling loop already IS the
 * source of truth for "was a check just made"). It answers "LAST
 * CHECKED", a genuinely different question from `updatedAtIso`'s "LAST
 * UPDATE", which keeps its own unchanged meaning and continues to drive
 * the stuck-detector below unchanged — a real stall in durable progress
 * is still exactly what that banner exists to catch, independent of
 * whether polling itself is alive.
 */

/** No durable update within this window while still "processing" is treated as stuck — never an animated bar spinning forever with no real signal behind it. Unchanged by the LAST CHECKED correction: this is deliberately about real progress staleness, not poll freshness. */
const STUCK_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Mandatory user frame selection (2026-09-19): "Retry automatic analysis"
 * selects a frame automatically and continues to Phota without the user
 * choosing one, which bypasses the rule that every Gate 1 run waits for an
 * explicit user frame choice. Its backend route is unwired (404), and the
 * button is hidden here. Code kept, not deleted.
 */
const RETRY_AUTOMATIC_ANALYSIS_ENABLED = false;

function useNowTicking(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const DISPLAY_STATUS_LABEL: Record<PersonaSourceStillProgress["displayStatus"], string> = {
  processing: "PROCESSING",
  ready_for_review: "READY FOR REVIEW",
  approved: "APPROVED",
  rejected: "REJECTED",
  failed: "FAILED",
  manual_reconciliation: "MANUAL RECONCILIATION",
  needs_attention: "NEEDS ATTENTION",
};

export function PersonaSourceStillProgressCard({
  progress,
  title,
  lastCheckedAtMs,
  tenantId,
  onRefresh,
  autoOpenPicker,
}: {
  readonly progress: PersonaSourceStillProgress;
  readonly title?: string;
  /** The caller's own `Date.now()` at its last SUCCESSFUL poll of this data — see this file's own top doc comment. `null` only in the (practically unreachable) case a caller renders this card before its own first successful fetch ever resolved. */
  readonly lastCheckedAtMs: number | null;
  /**
   * Gate 1 Manual Source Frame Picker (2026-09-16): both optional, and
   * both required together to show "Retry automatic analysis" /
   * "Choose frame manually" — a caller that only ever displays HISTORY
   * (never lets the user act) can render this card exactly as before by
   * simply omitting them. `tenantId` isn't on `PersonaSourceStillProgress`
   * itself (that's a derived, tenant-agnostic view), so it must come
   * from the caller, which always already has it in scope (URL param or
   * auth context).
   */
  readonly tenantId?: string;
  /** Re-fetches durable progress state — called after a successful retry or manual selection. Never assume success from the click alone. */
  readonly onRefresh?: () => Promise<void>;
  /**
   * Mandatory user frame selection (2026-09-19): when true, the Manual
   * Frame Picker opens by itself the first time this artifact is seen
   * waiting for the user's frame choice — so the user sees the frames
   * right after submitting, instead of having to find a button. Only the
   * Create tab (which shows the artifact the user just submitted) sets
   * this; REVIEWS lists many artifacts and must not open several modals.
   */
  readonly autoOpenPicker?: boolean;
}) {
  const { csrfToken, user } = useAuth();
  // Gate 1 text-cleanup failed - user decision (migration 125): the "Retry cleanup" request runs the whole new cleanup server-side (it can take up to ~5 minutes).
  const [cleanupRetrying, setCleanupRetrying] = useState(false);
  const [cleanupActionError, setCleanupActionError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryStartedAtMs, setRetryStartedAtMs] = useState<number | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Gate 1 "Choose subject" step (2026-09-25): opens right after a chosen frame that shows 2+ people, or from the "Choose subject" button while a choice is pending.
  const [subjectOpen, setSubjectOpen] = useState(false);
  // Mandatory user frame selection — the frame-selection request runs the
  // WHOLE of Gate 1 synchronously server-side (Phota + cleanup + identity
  // checks, ~3-4 minutes), so this card — not the picker modal — owns it:
  // the picker closes the instant the user chooses, and this card shows the
  // normal processing state while the request is in flight.
  const [selectingStartedAtMs, setSelectingStartedAtMs] = useState<number | null>(null);
  const [selectError, setSelectError] = useState<string | null>(null);
  const now = useNowTicking(1000);
  const createdAtMs = new Date(progress.createdAtIso).getTime();
  const updatedAtMs = new Date(progress.updatedAtIso).getTime();
  const elapsedMs = now - createdAtMs;
  const sinceDurableUpdateMs = now - updatedAtMs; // stuck-detector input only — never displayed directly, see top doc comment
  const sinceCheckedMs = lastCheckedAtMs !== null ? now - lastCheckedAtMs : null;
  // FIX-061 t517u (1): a select request sent by an EARLIER instance of this card (remount / reload while it was in flight) still counts as selecting,
  // so the picker is not offered again while the server has not yet recorded the choice. Expires by itself (see gate1-select-pending.ts).
  // It only applies while the durable state still says "awaiting your frame selection" (the server is checking the chosen frame); any other state wins.
  const durableAwaitingFrameSelection = progress.displayStatus === "manual_reconciliation" && progress.milestones.some((milestone) => milestone.key === "frame_selection" && milestone.state === "active");
  const persistedSelectingStartedAtMs = selectingStartedAtMs === null && durableAwaitingFrameSelection ? pendingFrameSelectionStartedAtMs(progress.artifactId, now) : null;
  const selecting = selectingStartedAtMs !== null || persistedSelectingStartedAtMs !== null;
  const checkingFrame = persistedSelectingStartedAtMs !== null;
  const isStuck = progress.displayStatus === "processing" && !selecting && sinceDurableUpdateMs > STUCK_THRESHOLD_MS;
  // While a chosen frame is being processed the card must already read as
  // "processing" (leave frame-selection mode immediately) — never keep
  // offering the choice, and never wait for the next poll to say so.
  const displayStatus: PersonaSourceStillProgress["displayStatus"] = selecting ? "processing" : progress.displayStatus;
  // Gate 1 identity verification FAILED: such an artifact must not read as a
  // normal, successful "READY FOR REVIEW" — see `derivePersonaSourceStillProgress`.
  const identityFailed = displayStatus === "ready_for_review" && progress.identityCheckFailed;
  // The chosen frame was rejected by the content-safety filter on BOTH base models: the run ended terminally
  // (`failed`, reason shown below), and "choose another frame" starts a new Gate 1 run (2026-09-25).
  const moderationBlocked = displayStatus === "failed" && progress.moderationBlockedOnBothModels === true;
  // The user rejected this result: it stays here, and "choose another frame" starts a new Gate 1 run (or the user removes it).
  const rejectedWithChoice = displayStatus === "rejected" && progress.rejectedWithFrameChoice === true;
  // The user can act (open the frame picker) while the artifact waits for a
  // frame choice — AND on a finished result whose FINAL cleaned Master failed
  // identity verification: it can never be approved, so "choose another
  // frame" (a new Gate 1 run) is the way forward.
  const canAct = (displayStatus === "manual_reconciliation" || identityFailed || moderationBlocked || rejectedWithChoice) && tenantId !== undefined && onRefresh !== undefined;
  const textCleanupFailed = displayStatus === "needs_attention" && tenantId !== undefined && onRefresh !== undefined;
  const retryingElapsedSeconds = retryStartedAtMs !== null ? Math.floor((now - retryStartedAtMs) / 1000) : 0;
  // `source_frame_extracted` is the only status that maps to
  // `manual_reconciliation` while the "Frame selection" milestone is the
  // active one (the two submission-uncertain statuses sit at later milestones).
  const awaitingFrameSelection = canAct && progress.milestones.some((milestone) => milestone.key === "frame_selection" && milestone.state === "active");
  const [pickerAutoOpened, setPickerAutoOpened] = useState(false);
  useEffect(() => {
    if (autoOpenPicker === true && awaitingFrameSelection && !pickerAutoOpened) {
      setPickerAutoOpened(true);
      setPickerOpen(true);
    }
  }, [autoOpenPicker, awaitingFrameSelection, pickerAutoOpened]);

  // Stale interaction state must not survive a backend state change:
  // - once durable state says the artifact is no longer in a state where the
  //   frame picker applies (`canAct` false), the picker's "open" flag is reset
  //   too — otherwise the modal would pop back up by itself if the artifact
  //   ever returned to that state;
  // - a previous selection error is dropped as soon as the durable state
  //   changes (`updatedAtIso`), because it described a state that is gone.
  useEffect(() => {
    if (!canAct) {
      setPickerOpen(false);
      setSubjectOpen(false);
    }
  }, [canAct]);
  useEffect(() => {
    setSelectError(null);
  }, [progress.updatedAtIso]);

  /** "Retry cleanup": one explicit user action - re-uses the stored Phota output, one new text-cleanup submit, never Phota/Head Swap (server side). */
  async function retryTextCleanup(): Promise<void> {
    if (tenantId === undefined || onRefresh === undefined) return;
    setCleanupRetrying(true);
    setCleanupActionError(null);
    try {
      await api.post(`/videos/${tenantId}/persona-source-stills/${progress.artifactId}/retry-text-cleanup`, csrfToken, {});
    } catch (error) {
      setCleanupActionError(error instanceof ApiError ? error.message : "Retry cleanup failed.");
    } finally {
      setCleanupRetrying(false);
    }
    await onRefresh();
  }

  /** "Remove / abandon": dismisses the card (the existing Gate 1 Reviews Remove) - no provider call, nothing is deleted. */
  async function removeFailedCleanup(): Promise<void> {
    if (tenantId === undefined || onRefresh === undefined) return;
    if (!window.confirm("Remove this card from Reviews? It will no longer appear here, but nothing is deleted.")) return;
    setCleanupActionError(null);
    try {
      await api.post(`/videos/${tenantId}/persona-source-stills/${progress.artifactId}/remove-from-reviews`, csrfToken, { removedBy: user?.email ?? "owner" });
    } catch (error) {
      setCleanupActionError(error instanceof ApiError ? error.message : "Remove failed.");
    }
    await onRefresh();
  }

  /**
   * Gate 1 Manual Source Frame Picker (2026-09-16), spec item 21: this
   * can genuinely take close to 4 minutes (120s x 2 Gemini attempts +
   * overhead) — the SAME reasoning as Create's own initial-submission
   * 5-minute message, reused verbatim here rather than inventing a
   * second wait-time UX.
   */
  async function retryAutomaticAnalysis(): Promise<void> {
    if (tenantId === undefined || onRefresh === undefined) return;
    setRetrying(true);
    setRetryStartedAtMs(Date.now());
    setRetryError(null);
    try {
      await api.post(`/videos/${tenantId}/persona-source-stills/${progress.artifactId}/source-frame-candidates/retry-analysis`, csrfToken, {});
      await onRefresh();
    } catch (error) {
      setRetryError(error instanceof ApiError ? error.message : "Retry failed.");
    } finally {
      setRetrying(false);
      setRetryStartedAtMs(null);
    }
  }

  /**
   * Mandatory user frame selection: called by the picker the instant the user
   * chooses a frame (the picker has already closed). Runs the long request
   * here, re-reads the durable state when it ends — success OR failure — and
   * never assumes the outcome from the click alone. A failed/lost response
   * (the server may still have finished the run) is followed by the same
   * re-read, so the card shows what really happened.
   */
  async function selectFrame(frame: { readonly frameIndex: number; readonly sha256: string }, path: "source-frame-candidates/select" | "target-subject/confirm" = "source-frame-candidates/select"): Promise<void> {
    if (tenantId === undefined || onRefresh === undefined) return;
    setSelectError(null);
    const startedAtMs = Date.now();
    setSelectingStartedAtMs(startedAtMs);
    markFrameSelectionPending(progress.artifactId, startedAtMs);
    // FIX-028: with the server's background loop the request only RECORDS the selection and answers 202 within moments (`frameSelectionQueued`); the run itself is shown by the durable status this
    // card re-reads and polls. The processing note is then kept until that first re-read has landed, so the card never flickers back to "waiting for your choice" in between.
    let queued = false;
    try {
      const result = await api.post<{ subjectSelectionRequired?: boolean; frameSelectionQueued?: boolean }>(`/videos/${tenantId}/persona-source-stills/${progress.artifactId}/${path}`, csrfToken, {
        frameIndex: frame.frameIndex,
        sha256: frame.sha256,
      });
      queued = result?.frameSelectionQueued === true;
      // The chosen frame shows 2+ people: nothing ran — the user now chooses which person is the target.
      if (result?.subjectSelectionRequired === true) setSubjectOpen(true);
    } catch (error) {
      setSelectError(error instanceof ApiError ? error.message : "Frame selection request failed — the current state is shown below.");
    } finally {
      if (!queued) {
        setSelectingStartedAtMs(null);
        clearFrameSelectionPending(progress.artifactId);
      }
    }
    await onRefresh();
    if (queued) {
      setSelectingStartedAtMs(null);
      clearFrameSelectionPending(progress.artifactId);
    }
  }

  return (
    <div className="progress-card">
      <div className="progress-header">
        <span className="progress-stage-label">{title ?? "GATE 1 — PREPARING COMPLETE LOOK"}</span>
        <span className={`badge status-${identityFailed ? "failed" : displayStatus}`}>{identityFailed ? "IDENTITY CHECK FAILED" : DISPLAY_STATUS_LABEL[displayStatus]}</span>
      </div>

      <MilestoneStrip milestones={progress.milestones} stuck={isStuck} />

      {isStuck ? (
        <div className="stuck-banner">
          PROCESSING DELAYED — no update in {formatRelative(sinceDurableUpdateMs)}
          {progress.manualReconciliationReason ? `: ${progress.manualReconciliationReason}` : ""}
        </div>
      ) : checkingFrame ? (
        <p className="muted" role="status" data-testid="gate1-frame-checking">
          Checking frame… your selection is being recorded. Please don&apos;t choose again — this card updates by itself.
        </p>
      ) : selecting ? (
        <p className="muted" role="status" data-testid="gate1-frame-selected-processing">
          Frame selected — Gate 1 is processing it now. This can take up to about 4 minutes; this card updates by itself, you don't need to keep this window open or refresh.
        </p>
      ) : displayStatus === "needs_attention" && progress.textCleanupSubmissionOutcomeUnknown === true ? (
        <div className="stuck-banner" data-testid="gate1-text-cleanup-outcome-unknown-banner">
          NEEDS ATTENTION — Gate 1 text cleanup: the previous paid cleanup attempt has an UNKNOWN outcome (it may already have been billed), so it is not retried automatically and Retry is not offered. You can remove this card.
        </div>
      ) : displayStatus === "needs_attention" ? (
        <div className="stuck-banner" data-testid="gate1-text-cleanup-failed-banner">
          NEEDS ATTENTION — Gate 1 text cleanup failed{progress.failureReason ? `: ${progress.failureReason.replace(/^Gate 1 text-cleanup failed:\s*/, "")}` : ""}
        </div>
      ) : displayStatus === "manual_reconciliation" ? (
        <div className="stuck-banner">NEEDS ATTENTION{progress.manualReconciliationReason ? `: ${progress.manualReconciliationReason}` : ""}</div>
      ) : displayStatus === "failed" ? (
        <div className="stuck-banner">FAILED{progress.failureReason ? `: ${progress.failureReason}` : ""}</div>
      ) : rejectedWithChoice ? (
        <div className="stuck-banner" data-testid="gate1-rejected-card-banner">
          REJECTED — you rejected this result. Choose another frame to run Gate 1 again, or remove it.
        </div>
      ) : identityFailed ? (
        <div className="error-banner" data-testid="gate1-identity-failed-card-banner">
          IDENTITY CHECK FAILED{progress.identityCheckFailureReason ? ` — ${progress.identityCheckFailureReason}` : ""}. You may choose another frame or approve this result manually.
        </div>
      ) : null}
      {/* The identity-failed result is still SHOWN (never withheld) so the user can see what failed. */}
      {identityFailed && tenantId !== undefined && (
        <img
          className="preview-image"
          data-testid="gate1-identity-failed-image"
          style={{ maxWidth: 260, marginTop: 8 }}
          src={`/videos/${tenantId}/persona-source-stills/${progress.artifactId}/preview?v=${encodeURIComponent(progress.updatedAtIso)}`}
          alt="Gate 1 result that failed identity verification"
        />
      )}
      {selectError && <div className="error-banner">{selectError}</div>}

      <div className="progress-meta">
        <div>
          <div className="label">Current stage</div>
          {/* Fix Gate 1 terminal-failure UI state (2026-09-15): a
              terminally FAILED artifact's `currentStageLabel` is still
              whatever milestone it was AT when it failed (e.g. "Head
              swap submitted") — accurate as history, but misleading as
              a "current stage" next to a FAILED badge, since nothing is
              still happening at that stage. Overridden ONLY for
              `displayStatus === "failed"` (the same field the badge
              above already uses) — every other status renders
              `currentStageLabel` completely unchanged. */}
          <div className="value">{checkingFrame ? "Checking frame…" : selecting ? "Processing the selected frame" : progress.displayStatus === "failed" ? "Gate 1 failed" : progress.currentStageLabel}</div>
        </div>
        <div>
          <div className="label">Elapsed</div>
          <div className="value">{formatElapsed(elapsedMs)}</div>
        </div>
        <div>
          <div className="label">Last checked</div>
          <div className="value">{sinceCheckedMs !== null ? formatRelative(sinceCheckedMs) : "—"}</div>
        </div>
        <div>
          <div className="label">Tracked cost</div>
          <div className="value">${progress.trackedCostUsd.toFixed(2)}</div>
        </div>
        {progress.requestedChangesDescription && (
          <div style={{ gridColumn: "1 / -1" }}>
            <div className="label">Requested</div>
            <div className="value">{progress.requestedChangesDescription}</div>
          </div>
        )}
      </div>

      {textCleanupFailed && (
        <div style={{ marginTop: 10 }} data-testid="gate1-text-cleanup-failed-actions">
          {cleanupActionError && <div className="error-banner">{cleanupActionError}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            {progress.textCleanupSubmissionOutcomeUnknown !== true && (
              <button disabled={cleanupRetrying} onClick={() => void retryTextCleanup()}>
                {cleanupRetrying ? "Retrying cleanup…" : "Retry cleanup"}
              </button>
            )}
            <button disabled={cleanupRetrying} onClick={() => void removeFailedCleanup()}>
              Remove
            </button>
          </div>
          {cleanupRetrying && (
            <p className="muted" style={{ marginTop: 6 }}>
              Re-running only the text cleanup on the stored result — this can take up to about 5 minutes. Please wait and don't click again.
            </p>
          )}
        </div>
      )}

      {canAct && (
        <div style={{ marginTop: 10 }}>
          {retryError && <div className="error-banner">{retryError}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            {RETRY_AUTOMATIC_ANALYSIS_ENABLED && (
              <button disabled={retrying} onClick={() => void retryAutomaticAnalysis()}>
                {retrying ? `Retrying… (${retryingElapsedSeconds}s)` : "Retry automatic analysis"}
              </button>
            )}
            {progress.subjectSelectionPending === true && (
              <button disabled={retrying} onClick={() => setSubjectOpen(true)}>
                Choose subject
              </button>
            )}
            <button disabled={retrying} onClick={() => setPickerOpen(true)}>
              {identityFailed || moderationBlocked || rejectedWithChoice ? "Choose another frame" : "Choose frame manually"}
            </button>
          </div>
          {retrying && (
            <p className="muted" style={{ marginTop: 6 }}>
              This can take up to about 5 minutes — please wait and don't click again.
            </p>
          )}
        </div>
      )}

      {canAct && subjectOpen && tenantId !== undefined && onRefresh !== undefined && (
        <SubjectSelector tenantId={tenantId} artifactId={progress.artifactId} onClose={() => setSubjectOpen(false)} onConfirm={(frame) => void selectFrame(frame, "target-subject/confirm")} />
      )}
      {canAct && pickerOpen && tenantId !== undefined && onRefresh !== undefined && (
        <ManualSourceFramePicker tenantId={tenantId} artifactId={progress.artifactId} onClose={() => setPickerOpen(false)} onSelect={(frame) => void selectFrame(frame)} />
      )}
    </div>
  );
}
